import { BuiltInAttribute } from "@orsane/schema/tasks";
import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import { UserSelect } from "@/components/property-selectors/UserSelect";
import { useChangeTaskAssigneesMutation } from "@/mutations/task-mutations";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";
import { useBulkTargetIds } from "../useBulkTargetIds";

function getGroupingValue(row: Row): string {
    // By encoding it, we take care of special chars in the name like a comma(which has its own meaning in the joining of the strings)
    if (row.data.assignees.length === 0) {
        return "";
    }
    return row.data.assignees.map((x) => `${x.id}_${encodeURIComponent(x.name)}`).join(", ") ?? "None";
}

// Static functions - don't need to be recreated
const getSortValue = ({ row }: { row: Row }) => {
    return getGroupingValue(row);
};

const getGroupHeader = ({ row }: { row: Row }) => {
    const groupingValue = getGroupingValue(row);
    if (groupingValue === "") {
        return "None";
    }
    return (
        groupingValue
            .split(", ")
            .map((x) => decodeURIComponent(x.split("_")[1]))
            .join(", ") ?? "None"
    );
};

// Memoized cell component
const AssigneeCell = memo(function AssigneeCell({
    row,
    onAssigneesChange,
}: {
    row: Row;
    onAssigneesChange: (taskId: number, assignees: any[]) => void;
}) {
    const handleChange = useCallback(
        (assignees: any[]) => {
            onAssigneesChange(row.data.id, assignees);
        },
        [row.data.id, onAssigneesChange]
    );

    return (
        <UserSelect
            trigger={(content) => (
                <CellButton disabled={row.data.isNewlyCreated}>
                    <div className="flex w-full items-center gap-1 overflow-hidden mask-linear-90 mask-linear-from-[calc(100%-20px)]">{content}</div>
                </CellButton>
            )}
            value={row.data.assignees}
            onChange={handleChange}
        />
    );
});

export function useAssigneeColumnBuilder() {
    const { mutate } = useChangeTaskAssigneesMutation();
    const getTargetIds = useBulkTargetIds();

    // Stable mutation handler
    const handleAssigneesChange = useCallback(
        (taskId: number, assignees: any[]) => {
            for (const id of getTargetIds(taskId)) {
                mutate({
                    id,
                    assignees: assignees,
                });
            }
        },
        [mutate, getTargetIds]
    );

    return {
        build: useCallback(
            (isVisible: boolean): ColumnDef => {
                return {
                    id: BuiltInAttribute.assignee,
                    header: "Assignee",
                    isVisible: isVisible,
                    cssColDef: "100px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <AssigneeCell row={row} onAssigneesChange={handleAssigneesChange} />,
                };
            },
            [handleAssigneesChange]
        ),
    };
}
